import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import {
    Button,
    Card,
    CardBody,
    Col,
    Container,
    FormFeedback,
    Input,
    Row,
} from "reactstrap";
import * as Yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { normalizeMetadataFilterDateForApi } from "helpers/metadataFilterHelpers";

import LeadsAddedReportTable from "components/Common/LeadsAddedReportTable";
import Loaders from "components/Common/Loaders";
import { AsyncClientSelect } from "components/Common/AsyncSearchSelects";
import {
    applyClientAsyncFilterOptionToFormState,
    buildClientAsyncFilterInitialOption,
    getClientAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import { submitClientStatusReportFilterData } from "model/ClientStatusReportModel";

const BASE_TABLE_COLUMNS = [
    { Header: "SN", accessor: "sn" },
    { Header: "Client", accessor: "client" },
];

const TRAILING_TABLE_COLUMNS = [
    { Header: "Last Day when email was sent", accessor: "lastDayWhenEmailWasSent" },
];

const normalizeStatusKey = (value) =>
    String(value ?? "")
        .trim()
        .toLowerCase();

const buildStatusAccessor = (index) => `status_${index}`;

const isReviewStatusColumn = (statusKey = "", statusLabel = "") => {
    const normalizedKey = normalizeStatusKey(statusKey);
    const normalizedLabel = normalizeStatusKey(statusLabel);
    return normalizedKey === "review status" || normalizedLabel === "review status";
};

const extractStatusColumnDefinitions = (rows = []) => {
    const list = Array.isArray(rows) ? rows : [];
    const firstRowWithStatusColumns = list.find(
        (row) => Array.isArray(row?.statusColumns) && row.statusColumns.length
    );
    if (!firstRowWithStatusColumns) return [];

    return firstRowWithStatusColumns.statusColumns
        .map((statusColumn = {}) => {
            const statusKey = String(statusColumn?.statusKey ?? "").trim();
            return {
                statusKey,
                statusLabel: String(statusColumn?.statusLabel ?? statusKey).trim(),
            };
        })
        .filter((definition) => definition.statusKey);
};

const buildTableColumns = (statusColumnDefinitions = []) => {
    const statusColumns = (Array.isArray(statusColumnDefinitions)
        ? statusColumnDefinitions
        : []
    ).map((definition, index) => ({
        Header: definition.statusLabel,
        accessor: buildStatusAccessor(index),
        statusKey: definition.statusKey,
        isReviewStatus: isReviewStatusColumn(definition.statusKey, definition.statusLabel),
        isStatusValue: !isReviewStatusColumn(definition.statusKey, definition.statusLabel),
    }));

    return [...BASE_TABLE_COLUMNS, ...statusColumns, ...TRAILING_TABLE_COLUMNS];
};

const strOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized === "" ? null : normalized;
};

const clientStatusReportFilterSchema = Yup.object().shape({
    fromDate: Yup.string().trim().required("Start date is required"),
    toDate: Yup.string().trim().required("End date is required"),
});

const FilterFieldFeedback = ({ message = "" }) => (
    <div className="filter-field-feedback-slot" aria-live="polite">
        {message ? (
            <FormFeedback type="invalid" className="d-block filter-field-feedback mb-0">
                {message}
            </FormFeedback>
        ) : null}
    </div>
);

FilterFieldFeedback.propTypes = {
    message: PropTypes.string,
};

const formatDateValue = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("MM/DD/YYYY") : String(value);
};

const resolveStatusDisplayValue = (statusColumn = {}) => {
    if (statusColumn?.hasValue === false) return "";
    const displayValue = statusColumn?.displayValue;
    if (displayValue === null || displayValue === undefined) return "";
    return displayValue;
};

const mapRowsFromApi = (rows = [], statusColumnDefinitions = []) => {
    const definitions = Array.isArray(statusColumnDefinitions) ? statusColumnDefinitions : [];
    const statusKeyToIndex = new Map(
        definitions.map((definition, index) => [
            normalizeStatusKey(definition.statusKey),
            index,
        ])
    );

    return (Array.isArray(rows) ? rows : []).map((item = {}, index) => {
        const mappedRow = {
            ...item,
            sn: item?.sn ?? item?.serialNumber ?? item?.sN ?? index + 1,
            client: item?.client ?? item?.clientName ?? "",
            clientId: item?.clientId ?? "",
            lastDayWhenEmailWasSent:
                item?.lastEmailSentRelative ??
                item?.lastDayWhenEmailWasSent ??
                item?.last_day_when_email_sent ??
                formatDateValue(item?.lastEmailSentIso ?? item?.lastEmailSentDate),
        };

        definitions.forEach((_, labelIndex) => {
            mappedRow[buildStatusAccessor(labelIndex)] = "";
        });

        (Array.isArray(item?.statusColumns) ? item.statusColumns : []).forEach(
            (statusColumn = {}) => {
                const labelIndex = statusKeyToIndex.get(
                    normalizeStatusKey(statusColumn?.statusKey)
                );
                if (labelIndex === undefined) return;
                mappedRow[buildStatusAccessor(labelIndex)] =
                    resolveStatusDisplayValue(statusColumn);
            }
        );

        return mappedRow;
    });
};

const extractFilterResponse = (response = {}) => {
    const responseData = response?.data ?? response ?? {};
    const nestedData =
        responseData?.data && typeof responseData.data === "object"
            ? responseData.data
            : responseData;

    const rows = Array.isArray(nestedData?.rows)
        ? nestedData.rows
        : Array.isArray(responseData?.rows)
            ? responseData.rows
            : Array.isArray(responseData?.data)
                ? responseData.data
                : Array.isArray(responseData?.records)
                    ? responseData.records
                    : [];

    return { rows };
};

const ClientStatusReport = () => {
    const [filterForm, setFilterForm] = useState({
        fromDate: "",
        toDate: "",
        clientId: "",
        [getClientAsyncFilterLabelFieldKey()]: "",
    });
    const [reportData, setReportData] = useState([]);
    const [statusColumnDefinitions, setStatusColumnDefinitions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterErrors, setFilterErrors] = useState({});

    const filteredData = useMemo(() => reportData, [reportData]);
    const tableColumns = useMemo(
        () => buildTableColumns(statusColumnDefinitions),
        [statusColumnDefinitions]
    );

    const buildFilterPayload = () => ({
        clientId: strOrNull(filterForm?.clientId),
        status: null,
        dateFrom: normalizeMetadataFilterDateForApi(filterForm?.fromDate),
        dateTo: normalizeMetadataFilterDateForApi(filterForm?.toDate),
    });

    const getClientStatusReportList = async () => {
        try {
            await clientStatusReportFilterSchema.validate(filterForm, { abortEarly: false });
            setFilterErrors({});
        } catch (validationError) {
            const nextErrors = {};
            validationError?.inner?.forEach((error) => {
                nextErrors[error.path] = error.message;
            });
            setFilterErrors(nextErrors);
            return;
        }

        setLoading(true);
        try {
            const response = await submitClientStatusReportFilterData(buildFilterPayload());
            const { rows } = extractFilterResponse(response);

            if (response == null || response?.success === false) {
                setReportData([]);
                setStatusColumnDefinitions([]);
                if (response?.message) {
                    toast.error(response.message);
                }
                return;
            }

            const nextStatusColumnDefinitions = extractStatusColumnDefinitions(rows);
            setStatusColumnDefinitions(nextStatusColumnDefinitions);
            setReportData(mapRowsFromApi(rows, nextStatusColumnDefinitions));
        } catch (error) {
            toast.error(error?.message || "Unable to load client status report");
            setReportData([]);
            setStatusColumnDefinitions([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterFieldChange = (event) => {
        const { name, value } = event.target;
        setFilterForm((previous) => ({
            ...previous,
            [name]: value,
        }));
        if (filterErrors?.[name]) {
            setFilterErrors((previous) => ({
                ...previous,
                [name]: "",
            }));
        }
    };

    return (
        <React.Fragment>
            <div className="page-content client-status-report-page">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <h5 className="fw-bold mb-3">Client Status Report</h5>
                            <hr />

                            <LegacyFilterToolbar className="w-100">
                                <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Start Date</span>
                                        <Input
                                            type="date"
                                            name="fromDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.fromDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.fromDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.fromDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">End Date</span>
                                        <Input
                                            type="date"
                                            name="toDate"
                                            className={`${LEGACY_FILTER_INPUT_CLASS}${
                                                filterErrors?.toDate ? " is-invalid" : ""
                                            }`}
                                            value={filterForm?.toDate || ""}
                                            onChange={handleFilterFieldChange}
                                        />
                                        <FilterFieldFeedback message={filterErrors?.toDate} />
                                    </Col>
                                    <Col xs="12" md="6" lg={3}>
                                        <span className="manage-user-filter-label">Client</span>
                                        <AsyncClientSelect
                                            key={`client-status-client-${filterForm?.clientId || ""}`}
                                            pageName="ReportUnrestricted"
                                            value={filterForm?.clientId || ""}
                                            initialOption={buildClientAsyncFilterInitialOption(
                                                filterForm?.clientId,
                                                filterForm[getClientAsyncFilterLabelFieldKey()]
                                            )}
                                            onChange={(option) =>
                                                setFilterForm((previous) =>
                                                    applyClientAsyncFilterOptionToFormState(
                                                        previous,
                                                        option
                                                    )
                                                )
                                            }
                                            placeholder="Select Client"
                                        />
                                        <FilterFieldFeedback />
                                    </Col>
                                    <Col xs="12" md="6" lg={2}>
                                        <span
                                            className="manage-user-filter-label"
                                            aria-hidden="true"
                                        >
                                            &nbsp;
                                        </span>
                                        <Button
                                            color="primary"
                                            className="w-100 manage-user-search-btn"
                                            type="button"
                                            onClick={getClientStatusReportList}
                                        >
                                            <i className="fa fa-search me-1" aria-hidden="true" />
                                            Search
                                        </Button>
                                        <FilterFieldFeedback />
                                    </Col>
                                </Row>
                            </LegacyFilterToolbar>

                            {loading ? (
                                <Loaders />
                            ) : (
                                <LeadsAddedReportTable
                                    columns={tableColumns}
                                    data={filteredData}
                                    pageName="ClientStatusReport"
                                />
                            )}
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

ClientStatusReport.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(ClientStatusReport);
